import type { TFunction } from 'i18next';
import { isAllowanceLine } from '../flat-rate/category';
import { formatShortDate } from './expense-labels';

/** A trip's row text: «Torino → Milano · 284 km»; null for any other expense. */
export function tripLine(expense: {
  readonly route?: string | null;
  readonly km?: string | null;
}): string | null {
  const route = expense.route?.trim();
  const km = expense.km?.trim();
  if (!route || !km) return null;
  return `${route} · ${km.replace(/\.0+$/, '').replace('.', ',')} km`;
}

/** A flat-rate allowance line's text: «Forfettario · 14/10/2026»; null for any other expense. */
export function allowanceLine(
  expense: { readonly category?: string | null; readonly date?: string | null },
  t: TFunction<'expenses'>,
): string | null {
  if (!isAllowanceLine(expense) || !expense.date) return null;
  return t('flatRate.line', { day: formatShortDate(expense.date) });
}

/**
 * What names an expense in a row, a card or the card's title: an allowance line's day, the
 * supplier; without one, a trip's route and km, or the description; «Senza fornitore» only when
 * all are empty.
 */
export function expenseName(
  expense: {
    readonly supplierName?: string | null;
    readonly description?: string | null;
    readonly route?: string | null;
    readonly km?: string | null;
    readonly category?: string | null;
    readonly date?: string | null;
  },
  t: TFunction<'expenses'>,
): string {
  const allowance = allowanceLine(expense, t);
  if (allowance !== null) return allowance;
  const supplier = expense.supplierName?.trim();
  if (supplier) return supplier;
  const trip = tripLine(expense);
  if (trip !== null) return trip;
  const description = expense.description?.trim();
  if (description) return description;
  return t('list.noSupplier');
}
